import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "../global/AuthContext";
import {
  activeNavigationGroup,
  navigationGroups,
  navigationItemActive,
} from "../global/navigation";
import "../assets/css/component/SidebarMenu.css";

export default function SidebarMenu({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const active = activeNavigationGroup(location.pathname);
  const [expanded, setExpanded] = useState(active);
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const groups = navigationGroups(
    !!user,
    user?.role?.toLowerCase() === "admin",
  );

  useEffect(() => {
    if (!isOpen) return;
    setExpanded(active);
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen, active]);

  if (!isOpen) return null;
  return (
    <>
      <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        className="sidebar open"
        id="site-sidebar"
        role="dialog"
        aria-modal="true"
        aria-label="전체 메뉴"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
          }
          if (event.key === "Tab") {
            const targets =
              panel.current?.querySelectorAll<HTMLElement>("button, a[href]");
            if (!targets?.length) return;
            const first = targets[0],
              last = targets[targets.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        }}
      >
        <div className="sidebar-heading">
          <span>전체 메뉴</span>
          <button
            ref={closeButton}
            className="sidebar-close"
            aria-label="메뉴 닫기"
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <nav className="sidebar-groups" aria-label="모바일 주 메뉴">
          {groups.map((group) => (
            <section className="sidebar-group" key={group.id}>
              <button
                className={`sidebar-group-trigger ${active === group.id ? "active" : ""}`}
                aria-expanded={expanded === group.id}
                aria-controls={`sidebar-links-${group.id}`}
                onClick={() =>
                  setExpanded(expanded === group.id ? "" : group.id)
                }
              >
                {group.label}
                <span aria-hidden="true">
                  {expanded === group.id ? "−" : "+"}
                </span>
              </button>
              {expanded === group.id && (
                <div className="sidebar-links" id={`sidebar-links-${group.id}`}>
                  {group.items.map((item) =>
                    item.logout ? (
                      <button
                        className="sidebar-logout"
                        key={item.label}
                        onClick={() => {
                          onClose();
                          logout();
                        }}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        key={item.label}
                        to={item.path}
                        state={
                          item.initialType
                            ? { initialType: item.initialType }
                            : undefined
                        }
                        aria-current={
                          navigationItemActive(
                            item,
                            location.pathname,
                            location.state,
                          )
                            ? "page"
                            : undefined
                        }
                        onClick={onClose}
                      >
                        {item.label}
                      </Link>
                    ),
                  )}
                </div>
              )}
            </section>
          ))}
          {!loading && !user && (
            <Link className="sidebar-login" to="/login" onClick={onClose}>
              로그인
            </Link>
          )}
        </nav>
      </div>
    </>
  );
}
